import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiBell, FiUser, FiShield, FiLogOut, FiMenu, FiCheckCircle, FiBookmark, FiTrash2, FiSettings, FiLock } from "react-icons/fi";
import { AuthContext } from "../../context/AuthContext";
import { NotificationContext } from "../../context/NotificationContext";
import { Avatar, Dropdown, DropdownItem, DropdownDivider } from "../ui";
import { getProfile } from "../../api/bios";
import uploadUrl from "../../utils/assetUrl";
import { formatSmartTimestamp } from "../../utils/format";
import { getNotificationTypeConfig } from "../../utils/notificationTypes";
import GlobalSearch from "./GlobalSearch";

export default function Topbar({ title, subtitle, onMenuClick }) {
  const { user, isAdmin, signOut } = useContext(AuthContext);
  const { notifications, unreadCount, markRead, markOne, deleteOne } = useContext(NotificationContext);
  const unreadNotifications = notifications.filter((n) => !n.read);
  const navigate = useNavigate();
  const [avatar, setAvatar] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  useEffect(() => {
    let active = true;
    if (!user?.id) return;
    getProfile(user.id).then(res => { if (active && res.data?.success) setAvatar(res.data.profile?.profileImage || null); }).catch(() => {});
    return () => { active = false; };
  }, [user?.id]);

  useEffect(() => {
    if (!notifOpen) return;
    const onClick = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [notifOpen]);

  const handleOpen = () => {
    setNotifOpen(o => !o);
  };

  const handleNotifClick = (n) => {
    markOne(n.id);
    setNotifOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6 lg:pl-6">
      <div className="flex min-w-0 items-center gap-3">
        <button onClick={onMenuClick} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden" aria-label="Open menu">
          <FiMenu className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h1 className="truncate text-[17px] font-semibold text-slate-900">{title}</h1>
          {subtitle && <p className="truncate text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>

      <div className="hidden flex-1 justify-center px-4 md:flex">
        <GlobalSearch />
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        <div className="relative" ref={notifRef}>
          <button onClick={handleOpen} aria-label="Notifications" className="relative rounded-full p-2 text-slate-500 hover:bg-slate-100">
            <FiBell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 z-50 mt-2 w-80 animate-fade-in overflow-hidden rounded-lg border border-slate-200 bg-white shadow-popover">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <p className="text-sm font-semibold text-slate-900">Notifications</p>
                {unreadCount > 0 && (
                  <button onClick={markRead} className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700">
                    <FiCheckCircle className="h-3 w-3" /> Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {/* Only unread notifications show here — the bell is a
                    quick "what's new" glance, so opening (or marking read)
                    one removes it from this list immediately. The full
                    history, read or not, stays browsable on the Inbox page. */}
                {unreadNotifications.length === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-slate-400">You're all caught up!</p>
                ) : (
                  unreadNotifications.map(n => {
                    const config = getNotificationTypeConfig(n.type);
                    const Icon = config.icon;
                    return (
                      <div
                        key={n.id}
                        onClick={() => handleNotifClick(n)}
                        className="group flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 bg-primary-50/40"
                      >
                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.color}`}>
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-slate-900 line-clamp-1">{n.title}</p>
                          <p className="mt-0.5 text-xs text-slate-500 line-clamp-1">{n.message}</p>
                          <p className="mt-1 text-[10px] text-slate-400">{formatSmartTimestamp(n.createdAt)}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteOne(n.id); }}
                          className="shrink-0 rounded-md p-1 text-slate-300 opacity-0 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                          title="Delete"
                        >
                          <FiTrash2 className="h-3.5 w-3.5" />
                        </button>
                    </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <Dropdown
          trigger={
            <button className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-slate-100">
              <Avatar src={avatar ? uploadUrl(avatar) : null} name={user?.name} size="sm" />
              <span className="hidden text-sm font-medium text-slate-700 sm:block">{user?.name}</span>
            </button>
          }
        >
          <div className="px-4 py-2">
            <p className="text-sm font-medium text-slate-800">{user?.name} {user?.lastName}</p>
            <p className="truncate text-xs text-slate-400">{user?.email}</p>
          </div>
          <DropdownDivider />
          <DropdownItem icon={<FiUser className="h-4 w-4" />} onClick={() => navigate("/profile")}>View profile</DropdownItem>
          <DropdownItem icon={<FiBookmark className="h-4 w-4" />} onClick={() => navigate("/saved")}>Saved posts</DropdownItem>
          <DropdownItem icon={<FiSettings className="h-4 w-4" />} onClick={() => navigate("/settings/notifications")}>Notification settings</DropdownItem>
          <DropdownItem icon={<FiLock className="h-4 w-4" />} onClick={() => navigate("/settings/password")}>Change password</DropdownItem>
          {isAdmin && <DropdownItem icon={<FiShield className="h-4 w-4" />} onClick={() => navigate("/admin")}>Admin portal</DropdownItem>}
          <DropdownDivider />
          <DropdownItem icon={<FiLogOut className="h-4 w-4" />} onClick={signOut} className="text-red-600 hover:bg-red-50 hover:text-red-700">Log out</DropdownItem>
        </Dropdown>
      </div>
    </header>
  );
}
